import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ShoppingBag, Sparkles, MapPin, MessageCircle, ArrowLeft, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WHATSAPP = '5566999584198';

// giraia-loja-v1
const CATEGORIAS = {
  sapatilha: '👟 Sapatilhas', bicicleta: '🚲 Bicicletas', bike_eletrica: '⚡ Bikes Elétricas',
  scooter: '🛴 Scooters e Patinetes', capacete: '⛑️ Capacetes', pecas: '🔧 Peças',
  acessorios: '🎒 Acessórios', vestuario: '👕 Vestuário', outro: '📦 Outros',
  acessorio_eletrica: '🔋 Acessórios Bike Elétrica', bike_eletrica_seminova: '♻️ Bikes Elétricas Seminovas',
  bike_seminova: '♻️ Bikes Seminovas'
};

// giraia-loja-v2: seções da vitrine (seção sem produto não aparece)
const temAro = (p, aros) => (p.tamanhos || []).some(t => aros.includes(t));
const ARO_INFANTIL = ['Aro 12', 'Aro 16', 'Aro 20', 'Aro 24'];
const CICLISMO = ['sapatilha', 'capacete', 'vestuario', 'pecas', 'acessorios', 'outro'];
const SECOES = [
  { id: 'acessorios-eletrica', titulo: 'Acessórios para bike elétrica', sub: 'Bateria, carregador, retrovisor, buzina e mais',
    filtro: p => p.categoria === 'acessorio_eletrica' },
  { id: 'eletricas-seminovas', titulo: 'Bikes elétricas seminovas', sub: 'Revisadas na nossa oficina',
    filtro: p => p.categoria === 'bike_eletrica_seminova' },
  { id: 'eletricas', titulo: 'Bikes elétricas e scooters', sub: 'Novas, com garantia',
    filtro: p => p.categoria === 'bike_eletrica' || p.categoria === 'scooter' },
  { id: 'aro-29', titulo: 'Bikes aro 29', sub: 'Mountain bike para trilha e cidade',
    filtro: p => p.categoria === 'bicicleta' && temAro(p, ['Aro 29']) },
  { id: 'aro-26', titulo: 'Bikes aro 26', sub: 'Para o dia a dia',
    filtro: p => p.categoria === 'bicicleta' && temAro(p, ['Aro 26']) },
  { id: 'infantis', titulo: 'Bikes infantis e juvenis', sub: 'Aro 12, 16, 20 e 24',
    filtro: p => p.categoria === 'bicicleta' && temAro(p, ARO_INFANTIL) },
  { id: 'outras-bikes', titulo: 'Outras bikes', sub: 'Aro 27.5 e outros modelos',
    filtro: p => p.categoria === 'bicicleta' && !temAro(p, ['Aro 29', 'Aro 26', ...ARO_INFANTIL]) },
  { id: 'seminovas', titulo: 'Bikes seminovas', sub: 'Revisadas e prontas para pedalar',
    filtro: p => p.categoria === 'bike_seminova' },
  { id: 'ciclismo', titulo: 'Acessórios para ciclismo', sub: 'Sapatilhas, capacetes, roupas, selins, peças e mais',
    filtro: p => CICLISMO.includes(p.categoria) },
];
const lerHash = () => decodeURIComponent((window.location.hash || '').replace('#', ''));
const brl = (v) => 'R$ ' + Number(v).toFixed(2).replace('.', ',');
const fotos = (p) => (p.imagens && p.imagens.length ? p.imagens : (p.imagem_url ? [p.imagem_url] : []));

const ProductCard = ({ product }) => {
  const [foto, setFoto] = useState(0);
  const [tamanho, setTamanho] = useState('');
  const imgs = fotos(product);
  const temPromo = product.preco_promocional && product.preco_promocional < product.preco;
  const precoFinal = temPromo ? product.preco_promocional : product.preco;
  const desconto = temPromo ? Math.round((1 - product.preco_promocional / product.preco) * 100) : 0;
  const esgotado = (product.estoque ?? 1) <= 0;
  const tamanhos = (product.tamanhos || []).filter(t => t !== 'Único');

  const chamar = () => {
    if (!esgotado && tamanhos.length > 1 && !tamanho) {
      toast.error(product.categoria === 'sapatilha' ? 'Escolha a numeração' : 'Escolha o tamanho');
      return;
    }
    const t = tamanho || (tamanhos.length === 1 ? tamanhos[0] : '');
    const msg = esgotado
      ? `Olá! Vi no site que o produto "${product.nome}" está esgotado. Pode me avisar quando chegar?`
      : `Olá! Tenho interesse em: ${product.nome}${t ? ` (${t})` : ''} - ${brl(precoFinal)}. Ainda tem disponível?`;
    window.open(`https://wa.me/${WHATSAPP}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  return (
    <div className="bg-[#120E1A] border border-[#2D243F] rounded-xl overflow-hidden hover:border-[#E2B84A]/50 transition-all flex flex-col">
      <div className="aspect-square bg-[#1C1628] overflow-hidden relative">
        {imgs[foto] && <img src={imgs[foto]} alt={product.nome} loading="lazy" className={`w-full h-full object-cover ${esgotado ? 'opacity-50' : ''}`} />}
        {desconto > 0 && !esgotado && <span className="absolute top-3 left-3 bg-[#10B981] text-white text-sm font-black px-3 py-1 rounded-full">-{desconto}%</span>}
        {product.destaque && !desconto && !esgotado && <span className="absolute top-3 left-3 bg-[#FFD700] text-black text-xs font-black px-3 py-1 rounded-full">⭐ DESTAQUE</span>}
        {esgotado && <span className="absolute top-3 left-3 bg-red-600 text-white text-xs font-black px-3 py-1 rounded-full">ESGOTADO</span>}
      </div>
      {imgs.length > 1 && (
        <div className="flex gap-2 px-4 pt-3 overflow-x-auto">
          {imgs.map((u, i) => (
            <button key={u} onClick={() => setFoto(i)} className={`w-12 h-12 shrink-0 rounded-md overflow-hidden border-2 ${i === foto ? 'border-[#E2B84A]' : 'border-transparent opacity-70'}`}>
              <img src={u} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
      <div className="p-4 flex flex-col flex-1">
        {product.marca && <p className="text-[#E2B84A] text-xs font-bold uppercase tracking-wide mb-1">{product.marca}</p>}
        <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{product.nome}</h3>
        {product.descricao && <p className="text-[#A19BAA] text-sm mb-3 whitespace-pre-line">{product.descricao}</p>}
        {tamanhos.length > 0 && !esgotado && (
          <div className="mb-3">
            <p className="text-[#716A80] text-xs mb-1">{product.categoria === 'sapatilha' ? 'Numeração:' : 'Tamanho:'}</p>
            <div className="flex flex-wrap gap-2">
              {tamanhos.map(t => (
                <button key={t} onClick={() => setTamanho(t)}
                  className={`px-3 py-1 rounded-md text-sm font-bold border ${tamanho === t ? 'bg-[#E2B84A] text-black border-[#E2B84A]' : 'border-[#2D243F] text-[#A19BAA] hover:border-[#E2B84A]'}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="mt-auto">
          {temPromo ? (
            <p className="mb-3">
              <span className="text-[#716A80] line-through mr-2">{brl(product.preco)}</span>
              <span className="text-2xl font-black text-[#FFD700]" style={{ fontFamily: 'Outfit, sans-serif' }}>{brl(product.preco_promocional)}</span>
            </p>
          ) : (
            <p className="text-2xl font-black text-[#FFD700] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{brl(product.preco)}</p>
          )}
          <button onClick={chamar}
            className={`w-full font-bold rounded-lg px-4 py-3 transition-colors flex items-center justify-center gap-2 ${esgotado ? 'bg-[#2D243F] text-white hover:bg-[#3a2f52]' : 'bg-[#25D366] text-white hover:bg-[#1fb857]'}`}>
            <MessageCircle size={18} />
            {esgotado ? 'Avise-me quando chegar' : 'Comprar pelo WhatsApp'}
          </button>
        </div>
      </div>
    </div>
  );
};

const PublicStorePage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [secao, setSecao] = useState(lerHash());
  const [sub, setSub] = useState('todos');

  useEffect(() => {
    document.title = 'Loja | Giraia Bike Shop - Sorriso-MT';
    axios.get(`${API_URL}/api/public/products`)
      .then(r => setProducts(r.data.products || []))
      .catch(() => toast.error('Erro ao carregar produtos'))
      .finally(() => setLoading(false));
  }, []);

  // o botão "voltar" do celular volta para as seções
  useEffect(() => {
    const aoMudar = () => { setSecao(lerHash()); setSub('todos'); window.scrollTo(0, 0); };
    window.addEventListener('hashchange', aoMudar);
    return () => window.removeEventListener('hashchange', aoMudar);
  }, []);

  const [abriuAqui, setAbriuAqui] = useState(false);
  const abrir = (id) => { setAbriuAqui(true); window.location.hash = id; };
  const voltar = () => {
    if (abriuAqui) { setAbriuAqui(false); window.history.back(); }
    else { window.location.hash = ''; }
  };

  const ordenados = useMemo(() => [...products].sort((a, b) =>
    ((b.destaque ? 2 : 0) + (b.preco_promocional ? 1 : 0) - ((b.estoque ?? 1) <= 0 ? 4 : 0)) -
    ((a.destaque ? 2 : 0) + (a.preco_promocional ? 1 : 0) - ((a.estoque ?? 1) <= 0 ? 4 : 0))), [products]);

  const secoes = useMemo(() => SECOES
    .map(s => ({ ...s, itens: ordenados.filter(s.filtro) }))
    .filter(s => s.itens.length > 0), [ordenados]);
  const promos = ordenados.filter(p => p.preco_promocional && (p.estoque ?? 1) > 0);

  let atual = null;
  if (secao === 'todos') atual = { id: 'todos', titulo: 'Todos os produtos', itens: ordenados };
  else if (secao === 'promo') atual = { id: 'promo', titulo: '🔥 Promoções', itens: promos };
  else atual = secoes.find(s => s.id === secao) || null;

  const subs = atual && atual.id === 'ciclismo'
    ? CICLISMO.filter(c => atual.itens.some(p => p.categoria === c)) : [];
  const lista = !atual ? [] : (sub === 'todos' ? atual.itens : atual.itens.filter(p => p.categoria === sub));

  const chip = (valor, texto) => (
    <button key={valor} onClick={() => setSub(valor)}
      className={`px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap border ${sub === valor ? 'bg-[#E2B84A] text-black border-[#E2B84A]' : 'border-[#2D243F] text-[#A19BAA] hover:border-[#E2B84A]'}`}>
      {texto}
    </button>
  );

  const capa = (itens) => { const p = itens.find(x => fotos(x).length); return p ? fotos(p)[0] : null; };

  return (
    <div className="min-h-screen bg-[#050505]">
      <div className="bg-gradient-to-br from-[#E2B84A]/20 to-[#FFD700]/10 p-6 border-b border-[#E2B84A]/30">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl md:text-5xl font-black text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Loja Giraia Bike Shop</h1>
          <p className="text-[#A19BAA] mb-3">Bikes, bikes elétricas, scooters, sapatilhas, peças e acessórios em Sorriso-MT.</p>
          <p className="text-[#E2B84A] text-sm flex items-center gap-2"><MapPin size={16} /> Retire na loja: Av. Ademar Raiter, 428, sala B, Centro Sul · ou combine a entrega pelo WhatsApp</p>
        </div>
      </div>

      <div className="p-6 max-w-7xl mx-auto">
        {loading ? (
          <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#E2B84A]"></div></div>
        ) : products.length === 0 ? (
          <div className="text-center py-20 bg-[#120E1A] border border-[#2D243F] rounded-xl">
            <ShoppingBag className="w-16 h-16 text-[#716A80] mx-auto mb-4" />
            <p className="text-[#A19BAA] mb-4">Estamos atualizando a vitrine. Fale com a gente pelo WhatsApp!</p>
            <a href={`https://wa.me/${WHATSAPP}`} target="_blank" rel="noreferrer" className="inline-block bg-[#25D366] text-white font-bold rounded-lg px-6 py-3">Chamar no WhatsApp</a>
          </div>
        ) : !atual ? (
          <>
            {promos.length > 0 && (
              <button onClick={() => abrir('promo')}
                className="w-full mb-6 bg-gradient-to-r from-[#E2B84A] to-[#FFD700] text-black rounded-xl p-5 flex items-center justify-between text-left">
                <span>
                  <span className="block text-xl md:text-2xl font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>🔥 Promoções da semana</span>
                  <span className="block text-sm font-bold">{promos.length} {promos.length === 1 ? 'produto' : 'produtos'} com desconto no PIX</span>
                </span>
                <ChevronRight size={28} />
              </button>
            )}
            <h2 className="text-white text-xl font-black mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>O que você procura?</h2>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              {secoes.map(s => {
                const img = capa(s.itens);
                return (
                  <button key={s.id} onClick={() => abrir(s.id)}
                    className="bg-[#120E1A] border border-[#2D243F] hover:border-[#E2B84A] rounded-xl overflow-hidden text-left flex flex-col transition-all">
                    <div className="aspect-[4/3] bg-[#1C1628] overflow-hidden">
                      {img ? <img src={img} alt={s.titulo} loading="lazy" className="w-full h-full object-cover" />
                        : <div className="w-full h-full flex items-center justify-center"><ShoppingBag className="w-10 h-10 text-[#716A80]" /></div>}
                    </div>
                    <div className="p-3 md:p-4">
                      <p className="text-white font-black text-base md:text-lg leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.titulo}</p>
                      <p className="text-[#A19BAA] text-xs md:text-sm mt-1 hidden sm:block">{s.sub}</p>
                      <p className="text-[#E2B84A] text-xs md:text-sm font-bold mt-1">{s.itens.length} {s.itens.length === 1 ? 'produto' : 'produtos'}</p>
                    </div>
                  </button>
                );
              })}
            </div>
            <button onClick={() => abrir('todos')} className="mt-6 w-full border border-[#2D243F] hover:border-[#E2B84A] text-[#E2B84A] font-bold rounded-xl px-4 py-4">
              Ver todos os produtos ({products.length})
            </button>
          </>
        ) : (
          <>
            <button onClick={voltar} className="inline-flex items-center gap-2 text-[#A19BAA] hover:text-white mb-4 py-2">
              <ArrowLeft size={20} /> Voltar para as seções
            </button>
            <h2 className="text-2xl md:text-3xl font-black text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>{atual.titulo}</h2>
            {subs.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-4 mb-2">
                {chip('todos', 'Todos')}
                {subs.map(c => chip(c, CATEGORIAS[c]))}
              </div>
            )}
            {lista.length === 0 ? (
              <div className="text-center py-16 bg-[#120E1A] border border-[#2D243F] rounded-xl">
                <p className="text-[#A19BAA] mb-4">Nenhum produto aqui agora. Fale com a gente que procuramos para você!</p>
                <a href={`https://wa.me/${WHATSAPP}`} target="_blank" rel="noreferrer" className="inline-block bg-[#25D366] text-white font-bold rounded-lg px-6 py-3">Chamar no WhatsApp</a>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {lista.map(p => <ProductCard key={p.id} product={p} />)}
              </div>
            )}
          </>
        )}
      </div>

      <div className="py-12 px-4 bg-[#120E1A] border-t border-[#2D243F]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-black text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Vai andar de scooter ou bike elétrica?</h2>
          <p className="text-[#A19BAA] mb-6">Faça grátis a primeira aula do nosso curso de segurança: leis de 2026, equipamentos e o que pode e o que não pode. Com certificado.</p>
          <Link to="/curso/gratis" className="inline-flex items-center gap-2 bg-[#E2B84A] hover:bg-[#F0D98C] text-black font-bold rounded-lg px-8 py-4 transition-colors">
            <Sparkles size={18} /> Fazer a aula grátis
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PublicStorePage;
